import {
  DEFAULT_SHAPE_STYLE,
  DEFAULT_TEXT_STYLE,
  STICKY_COLORS,
  clampViewport,
  type ArrowHead,
  type ConnectorRouting,
  type NodeId,
  type ShapeKind,
  type ShapeStyle,
  type StrokeStyle,
  type TextStyle,
  type Vec,
  type Viewport,
} from '@kanvas/board-core';
import { create } from 'zustand';

export type ToolId =
  | 'select'
  | 'hand'
  | 'shape'
  | 'sticky'
  | 'text'
  | 'draw'
  | 'eraser'
  | 'connector'
  | 'frame'
  | 'comment'
  | 'laser'
  | 'notebook';

export interface DrawStyle {
  color: string;
  size: number;
  brush: 'pen' | 'marker' | 'highlighter';
}

export interface ConnectorStyle {
  routing: ConnectorRouting;
  startHead: ArrowHead;
  endHead: ArrowHead;
  stroke: string;
  strokeWidth: number;
  strokeStyle: StrokeStyle;
}

export interface ContextMenuState {
  /** Screen position, in CSS pixels relative to the viewport. */
  x: number;
  y: number;
  /** Node the menu was opened on, null for the canvas menu. */
  nodeId: NodeId | null;
}

export interface EditorState {
  // ---- tools -------------------------------------------------------------
  tool: ToolId;
  /** Tool to fall back to after a one-shot creation, unless the user locked it. */
  toolLocked: boolean;
  shapeKind: ShapeKind;

  // ---- selection ---------------------------------------------------------
  selection: NodeId[];
  /** Groups the user has stepped into via double-click. */
  enteredGroups: string[];
  /** Notebook opened via double-click for paging / writing / drawing. */
  enteredNotebookId: NodeId | null;
  /** Image currently in crop mode. */
  croppingId: NodeId | null;
  /** Node whose text is being edited inline. */
  editingId: NodeId | null;
  /**
   * Board point the edit was opened at (a click on the text), so the caret
   * lands there rather than at the end. Null opens with the caret at the end.
   */
  editingCaret: Vec | null;
  /** Set while a gesture is running, so panels can avoid re-rendering. */
  interacting: boolean;
  /** Transient message shown over the board, e.g. a failed upload. */
  notice: { id: number; text: string } | null;

  // ---- camera ------------------------------------------------------------
  viewport: Viewport;

  // ---- styles for the next created object ---------------------------------
  shapeStyle: ShapeStyle;
  textStyle: TextStyle;
  stickyFill: string;
  drawStyle: DrawStyle;
  connectorStyle: ConnectorStyle;

  // ---- ui ----------------------------------------------------------------
  showMinimap: boolean;
  showGrid: boolean;
  snapEnabled: boolean;
  /** "Magic wand": snaps a closed/straight freehand stroke to a perfect shape. */
  shapeAssistEnabled: boolean;
  showComments: boolean;
  showParticipants: boolean;
  contextMenu: ContextMenuState | null;
  /** Peer whose camera we are mirroring, if any. */
  followingUserId: string | null;
  presentingFrameId: NodeId | null;

  // ---- actions -----------------------------------------------------------
  setTool: (tool: ToolId, options?: { lock?: boolean }) => void;
  toggleToolLock: () => void;
  setShapeKind: (kind: ShapeKind) => void;
  select: (ids: NodeId[]) => void;
  addToSelection: (ids: NodeId[]) => void;
  toggleInSelection: (id: NodeId) => void;
  clearSelection: () => void;
  enterGroup: (id: NodeId) => void;
  exitGroups: () => void;
  setEnteredNotebook: (id: NodeId | null) => void;
  setCropping: (id: NodeId | null) => void;
  setEditing: (id: NodeId | null, caretAt?: Vec | null) => void;
  notify: (text: string) => void;
  dismissNotice: (id: number) => void;
  setInteracting: (value: boolean) => void;
  setViewport: (vp: Viewport | ((prev: Viewport) => Viewport)) => void;
  patchShapeStyle: (patch: Partial<ShapeStyle>) => void;
  patchTextStyle: (patch: Partial<TextStyle>) => void;
  setStickyFill: (fill: string) => void;
  patchDrawStyle: (patch: Partial<DrawStyle>) => void;
  patchConnectorStyle: (patch: Partial<ConnectorStyle>) => void;
  setUi: (patch: Partial<Pick<EditorState,
    | 'showMinimap'
    | 'showGrid'
    | 'snapEnabled'
    | 'showComments'
    | 'showParticipants'
    | 'followingUserId'
    | 'presentingFrameId'
    | 'shapeAssistEnabled'>>) => void;
  openContextMenu: (state: ContextMenuState) => void;
  closeContextMenu: () => void;
}

const initialViewport: Viewport = {
  x: -400,
  y: -300,
  zoom: 1,
  width: 1,
  height: 1,
};

export const useEditor = create<EditorState>((set, get) => ({
  tool: 'select',
  toolLocked: false,
  shapeKind: 'rect',

  selection: [],
  enteredGroups: [],
  enteredNotebookId: null,
  croppingId: null,
  editingId: null,
  editingCaret: null,
  notice: null,
  interacting: false,

  viewport: initialViewport,

  shapeStyle: { ...DEFAULT_SHAPE_STYLE },
  textStyle: { ...DEFAULT_TEXT_STYLE },
  stickyFill: STICKY_COLORS[0].value,
  drawStyle: { color: '#1A1F28', size: 6, brush: 'pen' },
  connectorStyle: {
    routing: 'elbow',
    startHead: 'none',
    endHead: 'filledTriangle',
    stroke: '#1A1F28',
    strokeWidth: 2,
    strokeStyle: 'solid',
  },

  showMinimap: true,
  showGrid: true,
  snapEnabled: true,
  shapeAssistEnabled: false,
  showComments: false,
  showParticipants: false,
  contextMenu: null,
  followingUserId: null,
  presentingFrameId: null,

  setTool: (tool, options) =>
    set((s) => ({
      tool,
      toolLocked: options?.lock ?? s.toolLocked,
      editingId: null,
      croppingId: null,
      contextMenu: null,
      // Switching to a creation tool drops the selection so the style panel
      // shows the tool defaults rather than the previous object.
      selection: tool === 'select' || tool === 'hand' ? s.selection : [],
    })),

  toggleToolLock: () => set((s) => ({ toolLocked: !s.toolLocked })),
  setShapeKind: (shapeKind) => set({ shapeKind, tool: 'shape' }),

  select: (ids) => set({ selection: dedupe(ids) }),
  addToSelection: (ids) => set((s) => ({ selection: dedupe([...s.selection, ...ids]) })),
  toggleInSelection: (id) =>
    set((s) => ({
      selection: s.selection.includes(id)
        ? s.selection.filter((x) => x !== id)
        : [...s.selection, id],
    })),
  clearSelection: () => set({ selection: [], editingId: null, croppingId: null }),

  enterGroup: (id) =>
    set((s) => ({
      enteredGroups: s.enteredGroups.includes(id) ? s.enteredGroups : [...s.enteredGroups, id],
    })),
  exitGroups: () => set({ enteredGroups: [] }),

  setEnteredNotebook: (enteredNotebookId) =>
    set({ enteredNotebookId, editingId: null, croppingId: null }),
  setCropping: (croppingId) => set({ croppingId, editingId: null }),

  setEditing: (editingId, caretAt = null) =>
    set({ editingId, editingCaret: editingId ? caretAt : null, croppingId: null }),
  setInteracting: (interacting) => set({ interacting }),
  notify: (text) => set({ notice: { id: Date.now() + Math.random(), text } }),
  dismissNotice: (id) => set((s) => (s.notice?.id === id ? { notice: null } : {})),

  setViewport: (vp) =>
    set((s) => {
      const next = typeof vp === 'function' ? vp(s.viewport) : vp;
      return { viewport: clampViewport({ ...s.viewport, ...next }) };
    }),

  patchShapeStyle: (patch) => set((s) => ({ shapeStyle: { ...s.shapeStyle, ...patch } })),
  patchTextStyle: (patch) => set((s) => ({ textStyle: { ...s.textStyle, ...patch } })),
  setStickyFill: (stickyFill) => set({ stickyFill }),
  patchDrawStyle: (patch) => set((s) => ({ drawStyle: { ...s.drawStyle, ...patch } })),
  patchConnectorStyle: (patch) =>
    set((s) => ({ connectorStyle: { ...s.connectorStyle, ...patch } })),

  setUi: (patch) => set(patch),
  openContextMenu: (contextMenu) => set({ contextMenu }),
  closeContextMenu: () => {
    if (get().contextMenu) set({ contextMenu: null });
  },
}));

function dedupe(ids: readonly NodeId[]): NodeId[] {
  return [...new Set(ids)];
}

/** Non-reactive read, for use inside the render loop and pointer handlers. */
export const editorState = () => useEditor.getState();
